import { useId, useMemo } from "react";

export interface AnalyticsPieDatum {
  key: string;
  label: string;
  value: number;
}

interface AnalyticsPieChartProps {
  title: string;
  description: string;
  data: AnalyticsPieDatum[];
  emptyMessage: string;
  /**
   * Entity-stable slot assignment (1–6) for fixed domains — e.g. waste causes
   * always keep their color across periods. Keys without a slot are assigned
   * remaining slots in stable (alphabetical-key) order.
   */
  slotByKey?: Record<string, number>;
}

/** Hues are never cycled: beyond six slices the rest folds into "Other". */
const MAX_SLICES = 6;
const OTHER_KEY = "__other__";

const eur = (value: number) =>
  value.toLocaleString(undefined, {
    style: "currency",
    currency: "EUR",
  });

function polarPoint(angle: number, radius: number) {
  const radians = ((angle - 90) * Math.PI) / 180;
  return {
    x: 110 + radius * Math.cos(radians),
    y: 110 + radius * Math.sin(radians),
  };
}

/**
 * G-9.3 item 2 (owner decision 2026-07-28): the chart STAYS a pie, in brand
 * colors. Slice colors come from the validated `--chart-N` tokens (both theme
 * sets pass the dataviz six-checks validator), so the pie re-themes with the
 * app. Identity is never color-alone: the full-label legend sits BESIDE the
 * pie and the page's data tables mirror every number. Slices keep a surface
 * gap; a single-category period renders one full segment with its legend row.
 */
export function AnalyticsPieChart({
  title,
  description,
  data,
  emptyMessage,
  slotByKey,
}: AnalyticsPieChartProps) {
  const titleId = useId();
  const rows = useMemo(
    () => data.filter((row) => Number.isFinite(row.value) && row.value > 0),
    [data],
  );

  // Fixed-order slot assignment: pinned slots first (entity-stable domains),
  // then remaining slots in stable key order — never cycled.
  const slots = useMemo(() => {
    const map = new Map<string, number>();
    const used = new Set<number>();
    if (slotByKey) {
      for (const row of rows) {
        const slot = slotByKey[row.key];
        if (slot && slot >= 1 && slot <= MAX_SLICES && !used.has(slot)) {
          map.set(row.key, slot);
          used.add(slot);
        }
      }
    }
    const unassigned = rows
      .filter((row) => !map.has(row.key))
      .sort((a, b) => a.key.localeCompare(b.key));
    let next = 1;
    for (const row of unassigned) {
      while (used.has(next)) next += 1;
      if (next > MAX_SLICES) break;
      map.set(row.key, next);
      used.add(next);
    }
    return map;
  }, [rows, slotByKey]);

  const display = useMemo(() => {
    const sliced = rows
      .filter((row) => slots.has(row.key))
      .sort((a, b) => b.value - a.value);
    const rest = rows.filter((row) => !slots.has(row.key));
    const otherValue = rest.reduce((sum, row) => sum + row.value, 0);
    return otherValue > 0
      ? [
          ...sliced,
          {
            key: OTHER_KEY,
            label: `Other (${rest.length})`,
            value: otherValue,
          },
        ]
      : sliced;
  }, [rows, slots]);

  const total = display.reduce((sum, row) => sum + row.value, 0);
  const fillFor = (key: string) =>
    key === OTHER_KEY ? "var(--chart-other)" : `var(--chart-${slots.get(key)})`;

  let angle = 0;

  return (
    <article className="card analytics-pie-card">
      <header className="analytics-pie-header">
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <strong>{eur(total)}</strong>
      </header>

      {display.length > 0 ? (
        <div className="analytics-pie-content">
          <figure className="analytics-pie-figure">
            <svg
              className="analytics-pie-svg"
              viewBox="0 0 220 220"
              role="img"
              aria-labelledby={titleId}
            >
              <title id={titleId}>{title}</title>
              {display.length === 1 ? (
                <circle cx="110" cy="110" r="96" fill={fillFor(display[0].key)}>
                  <title>{`${display[0].label} — ${eur(display[0].value)} (100%)`}</title>
                </circle>
              ) : (
                display.map((row) => {
                  const startAngle = angle;
                  const sweep = (row.value / total) * 360;
                  const endAngle = startAngle + sweep;
                  angle = endAngle;
                  const start = polarPoint(startAngle, 96);
                  const end = polarPoint(endAngle, 96);
                  const path = [
                    "M 110 110",
                    `L ${start.x} ${start.y}`,
                    `A 96 96 0 ${sweep > 180 ? 1 : 0} 1 ${end.x} ${end.y}`,
                    "Z",
                  ].join(" ");

                  return (
                    <path
                      key={row.key}
                      d={path}
                      fill={fillFor(row.key)}
                      stroke="var(--bg-surface)"
                      strokeWidth="3"
                    >
                      <title>{`${row.label} — ${eur(row.value)} (${((row.value / total) * 100).toFixed(1)}%)`}</title>
                    </path>
                  );
                })
              )}
            </svg>
          </figure>

          <ul className="analytics-pie-legend">
            {display.map((row) => (
              <li key={row.key}>
                <span
                  className="analytics-pie-swatch"
                  data-slot={row.key === OTHER_KEY ? "other" : slots.get(row.key)}
                  aria-hidden="true"
                />
                <span className="analytics-pie-label">{row.label}</span>
                <span className="analytics-pie-value">
                  <strong>{eur(row.value)}</strong>
                  <small>{((row.value / total) * 100).toFixed(1)}%</small>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="analytics-pie-empty">{emptyMessage}</div>
      )}
    </article>
  );
}
